import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { Dropdown } from '@shared/ui-react/Dropdown';
import { Ajax } from '@shared/Ajax';
import { Layer, layerStore, measurementStore } from '@openlayers/tools/volume/tool-volumes-store';
import { VolumeThreejs } from '@openlayers/tools/volume/volume-object-threejs';
import { TreeNode } from '@shared/ui-native/Tree';
import "./tool-volumes-dialog.css";
import { Button } from '@shared/ui-react/Button'
import { ColorLegend } from './tool-volumes-helpers';
import { MapToolsStore } from '@openlayers/MapToolsStore';


export const VolumeCalculationConfig = observer(({ toolsStore }: { toolsStore: MapToolsStore }) => {
    const [showDemDetails, setShowDemDetails] = useState(false);
    const [density, setDensity] = useState(1.2);
    const [referenceOption, setReferenceOption] = useState("");
    const [targetOption, setTargetOption] = useState("");
    const [models, setModels] = useState([]);

    useEffect(() => {
        async function fetchData() {
            try {
                const data = await Ajax({ url: '/v1/map-baselayer/list' });
                layerStore.setLayers(data.baselayers.filter((layer) => layer.demUrl).map((layer) => ({ ...layer, text: layer.name })));
                const modelData = toolsStore.getSelectedModels();
                setModels(modelData);
            } catch (error) {
                console.error('Error fetching layers:', error);
            }
        }
        fetchData();
    }, []);

    const selectReference = (reference: TreeNode | Layer) => {
        layerStore.setReference(reference);
    };

    const selectTarget = (target: TreeNode | Layer) => {
        layerStore.setTarget(target);
    };

    const clearReference = () => {
        layerStore.setReference(null);
        setReferenceOption("");
    }

    const clearTarget = () => {
        layerStore.setTarget(null);
        setTargetOption("");
    }

    const selectMeasurement = (id) => {
        measurementStore.selectMeasurement(id);
    };

    const handleToleranceChange = (measurement, value) => {
        try {
            const { baseHeight, feature } = measurement;
            const { selectedMeasurementIndex } = measurementStore;

            const params = {
                feature,
                updateMeasurement: true,
                id: selectedMeasurementIndex,
                customTolerance: value,
                customBaseHeight: baseHeight,
            };

            measurement.tolerance = value;
            measurementStore.updateMeasurement(measurement);
            toolsStore.volumeTool.calculateVolume(params);
        } catch (error) {
            console.error('Error updating Tolerance:', error);
        }
    };

    const handleBaseHeightChange = (measurement, value) => {
        try {
            const { tolerance, feature } = measurement;
            const { selectedMeasurementIndex } = measurementStore;

            const params = {
                feature,
                updateMeasurement: true,
                id: selectedMeasurementIndex,
                customBaseHeight: value,
                customTolerance: tolerance,
            };

            measurement.baseHeight = value;
            measurementStore.updateMeasurement(measurement);
            toolsStore.volumeTool.calculateVolume(params);
        } catch (error) {
            console.error('Error updating BaseHeight:', error);
        }
    };

    const handleDensityChange = (e) => {
        setDensity(parseFloat(e.target.value) || 0);
    };

    const toggleDemDetails = () => {
        setShowDemDetails((prevState) => !prevState);
    };

    const handleFileUpload = async (e) => {
        const file = e.target.files?.[0];
        if (file) {
            try {
                const fileContent = await file.text();
                toolsStore.volumeTool.generateDrawingFromKml(fileContent);
            } catch (error) {
                console.error('Error reading KML file:', error);
            }
        }
    };

    const { layers, target, reference } = layerStore;
    const { measurements, selectedMeasurementIndex } = measurementStore;

    let measurement,
        demModelName: string,
        cutVolume: number,
        fillVolume: number,
        base64Image: string,
        demBase64Image: string,
        modelBase64Glb: string | undefined,
        baseHeight,
        tolerance,
        color,
        lowestPoint,
        highestPoint;

    if (selectedMeasurementIndex !== null) {
        measurement = measurements[selectedMeasurementIndex - 1];
        ({
            demModelName,
            cutVolume,
            fillVolume,
            base64Image,
            demBase64Image,
            baseHeight,
            tolerance,
            color,
            lowestPoint,
            highestPoint,
            modelBase64Glb,
        } = measurement);

        if (baseHeight && baseHeight.toFixed(2) !== baseHeight.toString()) {
            baseHeight = parseFloat(baseHeight.toFixed(2));
        }
    }

    const fillMass = fillVolume * density;
    const cutMass = cutVolume * density;
    const totalMass = fillMass - cutMass;

    const measurementIndex = toolsStore.volumeToolType === "stockpile" ? 2 : 3;
    const measurementToolIndex = toolsStore.volumeToolType === "stockpile" ? 3 : 4;

    const SelectTool = ({ layers, models }) => (
        <div>
        {toolsStore.volumeToolType === "stockpile" ?
            <div>
                <p className="select-header">
                    1. {__('js_map_measuring_tools.select_dem')}
                </p>
                <div className="select-container">
                    <SelectTarget layers={layers} />
                </div>
            </div> :
            <div>
            <p className="select-header">{__('js_map_measuring_tools.select_surface1')}</p>
            <div className="select-container">
                {/*{!referenceOption && <Selection onSelect={setReferenceOption} />}*/}
                {!referenceOption && <Button className="select-button" onClick={() => setReferenceOption("dem")}>{__('js_map_measuring_tools.dem')}</Button>}
                {referenceOption === "dem" && <SelectReference layers={layers} />}
                {referenceOption === "model" && <SelectReference layers={models} />}
                {referenceOption && <Button className="select-button" onClick={() => clearReference()}>{__('js_map_measuring_tools.back')}</Button>}
            </div>

            <p className="select-header">{__('js_map_measuring_tools.select_surface2')}</p>
            <div className="select-container">
                {/*{!targetOption && <Selection onSelect={setTargetOption} />}*/}
                {!targetOption && <Button className="select-button" onClick={() => setTargetOption("model")}>{__('js_map_measuring_tools.model')}</Button>}
                {targetOption === "dem" && <SelectTarget layers={layers} />}
                {targetOption === "model" && <SelectTarget layers={models} />}
                {targetOption && <Button className="select-button" onClick={() => clearTarget()}>{__('js_map_measuring_tools.back')}</Button>}
            </div>
            </div>
        }
        </div>
    );

    const Selection = ({ onSelect }) => (
        <>
            <Button className="select-button" onClick={() => onSelect("dem")}>{__('js_map_measuring_tools.dem')}</Button>
            <Button className="select-button" onClick={() => onSelect("model")}>{__('js_map_measuring_tools.model')}</Button>
        </>
    );

    const SelectReference = ({layers}) => {
        return (
            <div>
                <Dropdown className="select-file" closeOnBodyClick>
                <span className="toolbar-optional-label">
                    <span>{reference ? reference.text : __('js_map_measuring_tools.select')}</span>
                    <i className="iconf-caret" />
                </span>
                    <DropdownList layers={layers} onSelect={selectReference} />
                </Dropdown>
            </div>
        )
    };

    const SelectTarget = ({ layers }) => (
        <div>
            <Dropdown className="select-file" closeOnBodyClick>
                <span className="toolbar-optional-label">
                    <span>{target ? target.text : __('js_map_measuring_tools.select')}</span>
                    <i className="iconf-caret" />
                </span>
                <DropdownList layers={layers} onSelect={selectTarget} />
            </Dropdown>
        </div>
    );

    const DropdownList = ({layers, onSelect}) => (
        <ul className="dropdown-list clickable">
            {layers.length ? (
                layers.map((layer) => (
                    <li key={layer.uuid} onClick={() => onSelect(layer)}>
                        {layer.text}
                    </li>
                ))
            ) : (
                <li>{__('js_map_measuring_tools.no_layers_available')}</li>
            )}
        </ul>
    )

    const AdditionalMeasurements = () => (
            <div className="measurement-info">
                <p>
                    {__('js_map_measuring_tools.density')}:
                    <input
                        type="number"
                        value={density.toFixed(2)}
                        onChange={handleDensityChange}
                        step={0.02}
                        min={0}
                        style={{ width: '80px', marginLeft: '10px' }}
                    /> {__('js_map_measuring_tools.tons')}{'/m³'}
                </p>
                <p>{__('js_map_measuring_tools.fill_mass')}: {fillMass.toFixed(2)} {__('js_map_measuring_tools.tons')}</p>
                <p>{__('js_map_measuring_tools.cut_mass')}: {cutMass.toFixed(2)} {__('js_map_measuring_tools.tons')}</p>
                <p>{__('js_map_measuring_tools.total_mass')}: {totalMass.toFixed(2)} {__('js_map_measuring_tools.tons')}</p>
                {toolsStore.volumeToolType === "stockpile" ? <p>{__('js_map_measuring_tools.dem_model')}: {demModelName}</p> : null}
            </div>
    )

    const MeasurementContainer = () => {
        const BaseHeight = () => (
            <div>
                {toolsStore.volumeToolType === 'stockpile' &&
                    <p>
                        {__('js_map_measuring_tools.base_height')}:
                        <input
                            type="number"
                            value={baseHeight}
                            onInput={(e) => {
                                const newValue = parseFloat(
                                    e.currentTarget.value
                                )
                                handleBaseHeightChange(
                                    measurement,
                                    newValue
                                )
                            }}
                            step={0.1}
                            style={{
                                width: '80px',
                                marginLeft: '10px',
                            }}
                        />{' m'}
                    </p>
                }
            </div>
        )

        return (
            <div>
                <div className="measurement-legend-container">
                    <div>
                        <p className="select-header">
                            {measurementToolIndex}. {__('js_map_measuring_tools.measurement')}:
                        </p>
                        <div className="measurement-info">
                            <p>
                                {__('js_map_measuring_tools.fill_volume')}: {fillVolume?.toFixed(2)} m³
                            </p>
                            <p>
                                {__('js_map_measuring_tools.cut_volume')}: {cutVolume?.toFixed(2)} m³
                            </p>
                            <p>
                                {__('js_map_measuring_tools.total_volume')}: {(fillVolume - cutVolume).toFixed(2)} m³
                                <Button
                                    element="div"
                                    className="clickable"
                                    tooltip={__(
                                        'js_map_measuring_tools.total_info'
                                    )}
                                >
                                    <i className="iki iki-info info-button"></i>
                                </Button>
                            </p>
                            <BaseHeight />
                            <p>
                                {__('js_map_measuring_tools.tolerance')}
                                :
                                <input
                                    type="number"
                                    value={tolerance}
                                    step={0.05}
                                    min={0}
                                    onInput={(e) => {
                                        const newValue = parseFloat(
                                            e.currentTarget.value
                                        )
                                        handleToleranceChange(
                                            measurement,
                                            newValue
                                        )
                                    }}
                                    style={{
                                        width: '80px',
                                        marginLeft: '10px',
                                    }}
                                />{' m'}
                            </p>
                            <div
                                className="measurement-button"
                                onClick={toggleDemDetails}
                            >
                                <i
                                    className={`pi ${
                                        showDemDetails
                                            ? 'pi-chevron-down'
                                            : 'pi-chevron-right'
                                    }`}
                                />
                                {showDemDetails
                                    ? __('js_map_measuring_tools.less')
                                    : __('js_map_measuring_tools.more')}
                            </div>
                        </div>
                    </div>
                    {color && (
                        <ColorLegend
                            colors={color}
                            baseHeight={baseHeight}
                            lowestPoint={lowestPoint}
                            highestPoint={highestPoint}
                        />
                    )}
                </div>
                {showDemDetails && <AdditionalMeasurements />}
            </div>
        )
    }

    return (
        <div className="volume-calculation-config">
            <SelectTool layers={layers} models={models} />
            <p className="select-header">
                {measurementIndex}. {__('js_map_measuring_tools.select_measurement')}
            </p>
            <div className="select-measurement-container">
                <Dropdown className="select-dropdown" closeOnBodyClick>
                            <span className="toolbar-optional-label">
                                <span>
                                    {selectedMeasurementIndex !== null
                                        ? __('js_map_measuring_tools.measurement') +
                                        ': ' +
                                        selectedMeasurementIndex
                                        : __('js_map_measuring_tools.selected_measurement')}
                                </span>
                                <i className="iconf-caret" />
                            </span>
                    <ul className="dropdown-list clickable">
                        {measurements.length ? (
                            measurements.map((measurement) => (
                                <li
                                    key={measurement.id}
                                    onClick={() =>
                                        selectMeasurement(measurement.id)
                                    }
                                >
                                    {__('js_map_measuring_tools.measurement')}: {measurement.id}
                                </li>
                            ))
                        ) : (
                            <li>
                                {__('js_map_measuring_tools.no_measurements_available')}
                            </li>
                        )}
                    </ul>
                </Dropdown>
                <input
                    type="file"
                    accept=".kml"
                    onChange={handleFileUpload}
                    id="file-upload"
                    className="file-input"
                />
                <label htmlFor="file-upload" className="file-upload-label">
                    <i className="iki iki-upload upload-icon"></i>
                    {__('js_map_measuring_tools.upload_kml')}
                </label>
            </div>

            {selectedMeasurementIndex !== null && (
                <MeasurementContainer />
            )}

            <div className="volume-threejs-container">
                {base64Image && demBase64Image && (
                    <VolumeThreejs
                        base64Image={base64Image}
                        demBase64Image={demBase64Image}
                        modelBase64Glb={modelBase64Glb}
                    />
                )}
            </div>
        </div>
    )
});

